import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import { getUserProducts } from '../../../../services/user';
import { updateProductStatus, closeProduct } from '../../../../services/product';
import { productStatus } from '../../../../services/admin';
import { Card, message, Popconfirm, Button, Modal, Input, Select, Upload, Form } from 'antd';
import { MUIPagination } from '../../../../components/ui/pagination';
import { PlusOutlined } from '@ant-design/icons';
import { isAuthenticated } from '../../../../services/auth';
import { allCategories, allLocations } from '../../../../services/admin';
import axios from 'axios';

const AdminUserProducts = () => {
  const { userId } = useParams();
  const countPerPage = 5;
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState([]);
  const [current, setCurrent] = useState(1);
  const [statuses, setStatuses] = useState([]);
  const [statusChangeLoading, setStatusChangeLoading] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    location: '',
    condition: '',
    images: []
  });
  const [loading, setLoading] = useState(false);
  const { user, token } = isAuthenticated();

  const loadProducts = async (page = 1) => {
    try {
      const res = await getUserProducts(userId, {});
      setProducts(res.data);
      setCurrent(page);
      const to = page * countPerPage;
      const from = to - countPerPage;
      setPagination(res.data.slice(from, to));
    } catch (err) {
      setProducts([]);
      setPagination([]);
    }
  };

  useEffect(() => {
    loadProducts(1);
    const fetchStatuses = async () => {
      try {
        const res = await productStatus();
        setStatuses(res.data);
      } catch (err) {
        setStatuses(['active', 'pending', 'closed']);
      }
    };
    fetchStatuses();
    allCategories().then(res => setCategories(res.data)).catch(() => setCategories([]));
    allLocations().then(res => setLocations(res.data)).catch(() => setLocations([]));
  }, [userId]);

  const handleStatusChange = async (e, productId) => {
    const status = e.target.value;
    setStatusChangeLoading(productId);
    try {
      await updateProductStatus({ productId, status, token });
      message.success('Status atualizado!', 2);
      loadProducts(current);
    } catch (err) {
      message.error('Erro ao atualizar status');
    }
    setStatusChangeLoading(null);
  };

  const handleClose = async (productId) => {
    try {
      await closeProduct(productId);
      message.success('Produto encerrado', 4);
      loadProducts(current);
    } catch (err) {
      message.error('Erro ao encerrar produto');
    }
  };

  const handleProductInput = (e) => {
    setProductForm({ ...productForm, [e.target.name]: e.target.value });
  };

  const handleProductSelect = (value, field) => {
    setProductForm({ ...productForm, [field]: value });
  };

  const handleImageChange = ({ fileList }) => {
    setProductForm({ ...productForm, images: fileList });
  };

  const handleProductRegister = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('name', productForm.name);
    formData.append('description', productForm.description);
    formData.append('price', productForm.price);
    formData.append('category', productForm.category);
    formData.append('location', productForm.location);
    formData.append('condition', productForm.condition);
    formData.append('adminRole', 'admin');
    productForm.images.forEach((file) => {
      if (file.originFileObj) formData.append('images', file.originFileObj);
    });
    try {
      await axios.post(
        `${process.env.REACT_APP_API}/admin/add-product/${userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      message.success('Produto cadastrado com sucesso!');
      setShowProductModal(false);
      setProductForm({ name: '', description: '', price: '', category: '', location: '', condition: '', images: [] });
      loadProducts(1);
    } catch (err) {
      message.error('Erro ao cadastrar produto');
    }
    setLoading(false);
  };

  return (
    <div className="admin-user-products admin-user-products-container">
      <h2>Produtos do Usuário</h2>
      <Link to='/admin/manage-users' className='btn btn-link mb-3'>Voltar</Link>
      <Button type='primary' className='mb-3' onClick={() => setShowProductModal(true)}>Cadastrar Produto</Button>
      <Modal
        title='Cadastrar Produto'
        open={showProductModal}
        onCancel={() => setShowProductModal(false)}
        onOk={handleProductRegister}
        confirmLoading={loading}
        okText='Cadastrar'
        cancelText='Cancelar'
      >
        <Form layout='vertical'>
          <Form.Item label='Nome' required>
            <Input name='name' value={productForm.name} onChange={handleProductInput} />
          </Form.Item>
          <Form.Item label='Descrição' required>
            <Input.TextArea name='description' value={productForm.description} onChange={handleProductInput} />
          </Form.Item>
          <Form.Item label='Preço' required>
            <Input name='price' type='number' value={productForm.price} onChange={handleProductInput} />
          </Form.Item>
          <Form.Item label='Categoria' required>
            <Select
              value={productForm.category}
              onChange={value => handleProductSelect(value, 'category')}
              options={categories.map(c => ({ label: c.name, value: c._id }))}
            />
          </Form.Item>
          <Form.Item label='Localização' required>
            <Select
              value={productForm.location}
              onChange={value => handleProductSelect(value, 'location')}
              options={locations.map(l => ({ label: l.name, value: l._id }))}
            />
          </Form.Item>
          <Form.Item label='Condição'>
            <Select
              value={productForm.condition}
              onChange={value => handleProductSelect(value, 'condition')}
              options={['Novo', 'Usado'].map(c => ({ label: c, value: c }))}
            />
          </Form.Item>
          <Form.Item label='Imagens'>
            <Upload
              listType='picture-card'
              fileList={productForm.images}
              onChange={handleImageChange}
              beforeUpload={() => false}
              multiple
            >
              {productForm.images.length >= 8 ? null : <div><PlusOutlined /> Adicionar</div>}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
      {pagination.length === 0 && <p>Nenhum produto encontrado.</p>}
      {pagination.map((p, i) => (
        <Card key={p._id} className='mb-3 admin-user-products-card'>
          <div className='d-flex justify-content-between'>
            <div>
              <h5>{p.name}</h5>
              <p>{p.description}</p>
              <p>Status: 
                <select
                  value={p.status}
                  onChange={e => handleStatusChange(e, p._id)}
                  disabled={statusChangeLoading === p._id}
                  className='ms-2'
                >
                  {statuses.map((s, idx) => (
                    <option key={idx} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </p>
              <p>Categoria: {p.category && p.category.name}</p>
              <p>Localização: {p.location && p.location.name}</p>
              <p>Preço: R$ {p.price}</p>
              <p>Criado em: {moment(p.createdAt).format('DD/MM/YYYY HH:mm')}</p>
            </div>
            <div className='d-flex flex-column align-items-end'>
              <Link to={`/admin/edit-product/${p._id}`} className='btn btn-info btn-sm mb-2'>Editar</Link>
              {p.status !== 'closed' && (
                <Popconfirm
                  placement='top'
                  title={'Tem certeza que deseja encerrar?'}
                  onConfirm={() => handleClose(p._id)}
                  okText='Sim'
                  cancelText='Não'
                >
                  <Button danger size='small'>Encerrar</Button>
                </Popconfirm>
              )}
            </div>
          </div>
        </Card>
      ))}
      <MUIPagination
        count={Math.ceil(products.length / countPerPage)}
        page={current}
        onChange={loadProducts}
        color="primary"
        size="medium"
        showFirstButton={Math.ceil(products.length / countPerPage) > 5}
        showLastButton={Math.ceil(products.length / countPerPage) > 5}
      />
    </div>
  );
};

export default AdminUserProducts;
